import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, MapPin, ExternalLink, Loader2, Instagram } from 'lucide-react';
import { motion } from 'framer-motion';
import SEO from './SEO';
import CategoryNav from './CategoryNav';
import SocialLinks, { countSocialLinks } from './SocialLinks';
import { useGooglePlaces, normalizeName, type PlaceResult } from '../hooks/useGooglePlaces';
import { usePreviewSafeData } from '../hooks/usePreviewSafeData';
// ── Inlined Food Truck data (formerly from FoodTrucks.tsx) ──
interface FoodTruck {
  name: string;
  city: string;
  address: string;
  addresses?: string[];
  website: string;
  image: string;
}

export const FOOD_TRUCKS: FoodTruck[] = [
  { name: "Dallaspuram Dhaba", city: "Denton", address: "209 N Carroll Blvd, Denton, TX 76201", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783454550541-ileu0y.png" },
  { name: "Golconda Xpress", city: "Denton", address: "400 South Elm Street, Denton, Texas 76201", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783373340863-69abps.png" },
  { name: "Babai Bandi", city: "Denton", address: "1028 S Welch St, Denton, TX 76201", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783456320795-hs5ar.png" },
  { name: "Indian Expresss", city: "Irving", address: "4850 W John Carpenter Fwy, Irving, TX 75063", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783458229171-d62xqi.png" },
  { name: "Maya Bazar", city: "Irving", address: "2628 North Story Rd Irving Texas 75062", website: "", image: "https://images.unsplash.com/photo-1565123409695-7b5ef63a2efb?w=600&h=400&fit=crop" },
  { name: "NH7 Dhaba", city: "Frisco", address: "8780 Preston Trace Blvd, Frisco, TX 75033", website: "", image: "https://images.unsplash.com/photo-1565123409695-7b5ef63a2efb?w=600&h=400&fit=crop" },
  { name: "NH7 Dhaba", city: "Little Elm", address: "3381 Farm to Market Rd 720, Little Elm, TX 75068", website: "", image: "https://images.unsplash.com/photo-1565123409695-7b5ef63a2efb?w=600&h=400&fit=crop" },
  { name: "NH7 Dhaba", city: "Denton", address: "420 S Carroll Blvd Ste 102, Denton, TX 76201", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783457577509-tbbbvr.png" },
];
// ── Inlined Hookah Lounge data (formerly from HookahLounges.tsx) ──
interface HookahLounge {
  name: string;
  city: string;
  address: string;
  website: string;
  image: string;
}

export const HOOKAH_LOUNGES: HookahLounge[] = [
  { name: "Huq Up Hookah Cafe", city: "Little Elm", address: "27052 E University Dr Ste 100, Little Elm, TX 75068", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783371008855-9gzzab.png" },
  { name: "Velvet Hookah Lounge", city: "Carrollton", address: "2741 E Belt Line Rd Ste 115, Carrollton, TX 75006", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1781220281533-kne6l8.png" },
  { name: "Ghazal Lounge", city: "McKinney", address: "4051 S Custer Rd Ste 1110, McKinney, TX 75070", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783458463796-e3cu.png" },
  { name: "Cavalli", city: "Dallas", address: "18111 Dallas Pkwy #100, Dallas, TX 75287", website: "", image: "https://images.unsplash.com/photo-1571896769668-a9e7d0b67b27?w=600&h=400&fit=crop" },
];
// ── Hardcoded custom restaurants (guaranteed to appear) ──
interface CustomRestaurant {
  name: string;
  city: string;
  address: string;
  website: string;
  image: string;
  cuisines?: string[];
  instagram?: string;
}

export const CUSTOM_RESTAURANTS: CustomRestaurant[] = [
  { name: "Alvi's Kitchen", city: "Carrollton", address: "2650 Midway Rd #114, Carrollton, TX 75006", website: "", image: "https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-22/25d55aa35ff7.png", cuisines: ["Pakistani", "Halal"] },
  { name: "Selda Mediterranean Kitchen", city: "Frisco", address: "9250 Dallas Pkwy Ste 130, Frisco, TX 75033", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784534372956-bus2j.jpg", cuisines: ["Mediterranean"] },
  { name: "Al Markaz", city: "Carrollton", address: "1205 W Trinity Mills Rd #112, Carrollton, TX 75006", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784688168153-prym5.png", cuisines: ["Pakistani", "Halal"] },
  { name: "Chalo India", city: "Frisco", address: "11010 Panther Creek Pkwy #1, Frisco, TX 75034", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784691566550-drnie7.png", cuisines: ["South Indian"] },
  { name: "Karahi Point Dallas", city: "Plano", address: "2432 Preston Rd Ste 340 A, Plano, TX 75093, United States", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782767592288-ql6w86.png", cuisines: ["Pakistani", "Halal"] },
  { name: "Pizza Twist", city: "Frisco", address: "6950 Lebanon Rd St 104, Frisco, TX 75034", website: "", image: "https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-30/8aa7004fdc28.png", cuisines: ["Pizza"] },
  { name: "Stadium Sports Grill", city: "Dallas", address: "4872 Belt Line Rd, Dallas, TX 75254, United States", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782932572657-l45t1.png", cuisines: ["Bar & Grill"] },
  { name: "PizzaOPizza", city: "Frisco", address: "25691 Smotherman Rd Ste 180, Frisco, TX 75034", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783460762496-60j60i.png", cuisines: ["Pizza"] },
  { name: "Curry Pizza House", city: "Frisco", address: "5266 Independence Pkwy Ste 100, Frisco, TX 75035", website: "", image: "https://images.unsplash.com/photo-1548365328-9f547fb0953b?w=600&h=400&fit=crop", cuisines: ["Pizza"] },
  { name: "Frisco BaseCamp", city: "Frisco", address: "2552 Stonebrook Pkwy #302, Frisco, TX 75034", website: "", image: "https://images.unsplash.com/photo-1544025162-d76694265947?w=600&h=400&fit=crop", cuisines: ["Fusion"] },
  { name: "Sheesh Eatery & Lounge", city: "The Colony", address: "4707 State Hwy 121, The Colony, TX 75056", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784069304944-unr3n.png", cuisines: ["Pakistani", "Hookah Lounge", "Halal"], instagram: "https://www.instagram.com/p/DG_74uHvBg0/" },
  { name: "Simply South", city: "Frisco", address: "8250 State Hwy 121, Frisco, TX 75034", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783370420569-sfgju6.png", cuisines: ["South Indian", "Vegetarian"] },
  { name: "Simply South", city: "Irving", address: "1001 MacArthur Park Dr, Irving, TX 75063", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783371095561-y3aal.png", cuisines: ["South Indian", "Vegetarian"], instagram: "https://www.instagram.com/p/DL44s4csLRH/" },
  { name: "Simply South", city: "McKinney", address: "4781 S Custer Rd, McKinney, TX 75070", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783370168275-vgxv9i.png", cuisines: ["South Indian", "Vegetarian"], instagram: "https://www.instagram.com/p/DThVFxVDe7K/" },
  { name: "Moonrise Lake Lounge", city: "Frisco", address: "8951 Lebanon Rd, Frisco, TX 75034", website: "", image: "https://images.unsplash.com/photo-1571896769668-a9e7d0b67b27?w=600&h=400&fit=crop", cuisines: ["Hookah Lounge", "Bar & Grill"], instagram: "https://www.instagram.com/p/Dbt7op9sYo7/" },
  { name: "Hyderabad House", city: "Prosper", address: "4740 W University Dr Ste 20, Prosper, TX 75078", website: "", image: "https://images.unsplash.com/photo-1589302168068-964664d93dc0?w=600&h=400&fit=crop", cuisines: ["South Indian"] },
  { name: "Desi District", city: "Frisco", address: "12990 Eldorado Pkwy #100, Frisco, TX 75035", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783371877943-e4eite.png", cuisines: ["South Indian"] },
  { name: "Habibi Royal Mandi House", city: "Arlington", address: "1615 W Park Row Dr, Arlington, TX 76013", website: "", image: "https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=600&h=400&fit=crop", cuisines: ["South Indian"] },
  { name: "Fusion Republic Indian Kitchen", city: "Richardson", address: "700 E Campbell Rd Ste 190, Richardson, TX 75081", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783455121202-gfyeso.png", cuisines: ["Fusion"] },
  { name: "Page 3 Eatery", city: "McKinney", address: "7560 State Hwy 121 Ste 100, McKinney, TX 75070", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783625302720-78bgih.png", cuisines: ["Fusion"] },
  { name: "Hashtag India", city: "Aubrey", address: "2965 FM1385 Ste 800, Aubrey, TX 76227", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783455507540-1e3vqt.png", cuisines: ["South Indian", "Street Food"] },
  { name: "Hashtag India", city: "Irving", address: "1102 W Lyndon B Johnson Fwy, Irving, TX 75063", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782223920185-tudhij.jpg", cuisines: ["South Indian", "Street Food"] },
  { name: "Hashtag India Lewisville", city: "Lewisville", address: "4440 State Hwy 121 Ste 5, Lewisville, TX 75056", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1788361344117-iubxdw.jpg", cuisines: ["South Indian", "Street Food"], instagram: "https://www.instagram.com/reel/Dcw2cZWM-a-/" },
  { name: "Indraprastha", city: "Little Elm", address: "850 W Eldorado Pkwy #300, Little Elm, TX 75068", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783455686881-1ur4p.png", cuisines: ["South Indian"] },
  { name: "Chennai Cafe", city: "Frisco", address: "3301 Preston Rd, Frisco, TX 75034", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783455749183-54fx5k.png", cuisines: ["South Indian"] },
  { name: "Hyderabad Wala", city: "McKinney", address: "2414 W University Dr Ste. 200, McKinney, TX 75071", website: "", image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-06/67cc0c3672fb.jpeg", cuisines: ["South Indian"] },
  { name: "The Pulao Company", city: "Frisco", address: "13089 Main St #400, Frisco, TX 75035", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783365332940-h8p7de.png", cuisines: ["South Indian"] },
  { name: "Anjappar Plano", city: "Plano", address: "4940 McDermott Rd #200, Plano, TX 75024", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783462697849-rwjd1b.png", cuisines: ["South Indian"], instagram: "https://www.instagram.com/p/DKDggutyaNN/" },
  { name: "Parotta Express", city: "Coppell", address: "820, #112, South Macarthur Blvd, Coppell, Texas 75019", website: "", image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-16/01236cafdfaf.jpeg", cuisines: ["South Indian"] },
  { name: "Lakeside Pizza Bar and Grill", city: "Little Elm", address: "1000 W Eldorado Pkwy Ste 800, Little Elm, TX 75068", website: "", image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-07/e5a761787dfd.jpeg", cuisines: ["Pizza", "Bar & Grill"] },
  { name: "Bollywood Pizza House", city: "Frisco", address: "2575 Main St, Suite #310, Frisco, TX 75036", website: "", image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-08/81a5bf20c68c.jpeg", cuisines: ["Pizza"], instagram: "" },
  { name: "Soul Foods India", city: "Frisco", address: "4681 Ohio Drive unit 105, Frisco, TX 75035", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783577121128-rjyi0j.png", cuisines: ["North Indian"] },
  { name: "Grand Tandoori Restaurant & Catering", city: "Plano", address: "4101 E Park Blvd. Ste 121, Plano, TX 75074", website: "", image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-09/a707533b706e.jpeg", cuisines: ["North Indian", "Pakistani", "Fusion", "Halal"] },
  { name: "Karahi Boys", city: "Frisco", address: "3401 Preston Rd Suite. 1, Frisco, TX 75034", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784190094829-x6ge9.png", cuisines: ["Pakistani", "Halal"] },
  { name: "Old City Food Truck & Mandi House", city: "Aubrey", address: "26785 E University Dr #700, Aubrey, TX 76227", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784283850379-qcekeo.png", cuisines: ["South Indian", "Food Trucks"] },
  { name: "Al-Mueed BBQ", city: "Plano", address: "4301 14th St Ste 230, Plano, TX 75074, United States", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784307865024-g7ehip.png", cuisines: ["Pakistani", "Halal"] },
  { name: "Desi Chichas", city: "Allen", address: "209 N Central Expy, Allen, TX 75013", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784309451315-0zg9c.jpg", cuisines: ["South Indian"] },
  { name: "Kashkan Dhaba", city: "Dallas", address: "17370 Preston Rd #490, Dallas, TX 75252", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784421226199-3hiwo.jpg", cuisines: ["North Indian", "South Indian"] },
  { name: "Santhis Kitchens", city: "Frisco", address: "2552 Stonebrook Pkwy #102, Frisco, TX, United States, 75034", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784309709600-1f4hwk.png", cuisines: ["South Indian"] },
  { name: "Haji Aslam Biryani", city: "Plano", address: "700 Spring Creek Pkwy, Plano, TX 75023, United States", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784311570884-2l4bad.png", cuisines: ["Pakistani", "Halal"] },
  { name: "Mythri's Indian Cuisine", city: "Irving", address: "8350 N MacArthur Blvd Ste 190, Irving, TX 75063, United States", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784352150491-y6qtuu.png", cuisines: ["South Indian"] },
  { name: "JAFFA JOINT", city: "Plano", address: "2400 K Ave B, Plano, TX 75074, United States", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784352338153-6cp5nl.png", cuisines: ["Mediterranean"] },
  { name: "Lyari Cafe", city: "Dallas", address: "11641 Harry Hines Blvd #201, Dallas, TX 75229, United States", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784352484266-8b16n.png", cuisines: ["Pakistani", "Halal"] },
  { name: "Cafe Paradise", city: "Frisco", address: "25691 Smotherman Rd #220, Frisco, TX 75033, United States", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784353664092-fwhucs.png", cuisines: ["Others"] },
  { name: "Foodistan Indian Restaurant", city: "Irving", address: "5400 Green Park Dr, Irving, Texas 75038", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784677628729-wkbb1j.png", cuisines: ["South Indian", "North Indian"] },
  { name: "Foodistaan - Groceries, Halal Meat & Eatery", city: "Aubrey", address: "27475 E University Dr, Aubrey, TX 76227", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784678143837-9p02l.png", cuisines: ["South Indian", "North Indian"] },
  { name: "Sai Bhavan", city: "Carrollton", address: "1016 E Hebron Pkwy Ste 148, Carrollton, TX", website: "", image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-23/85d6be453d34.jpeg", cuisines: ["North Indian", "South Indian"] },
  { name: "Sai Bhavan Prosper", city: "Prosper", address: "1640 W Frontier Pkwy Ste 120, Prosper, TX", website: "", image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-23/85d6be453d34.jpeg", cuisines: ["North Indian", "South Indian"] },
  { name: "GT Road", city: "Little Elm", address: "11700 FM 423 Unit 140, Little Elm, TX 75068", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1786992747509-m8ubvr.png", cuisines: ["Halal"], instagram: "https://www.instagram.com/reel/Db9db8JMRTF/" },
  { name: "Dwaraka Curry Point", city: "Frisco", address: "7151 Preston Rd #101a, Frisco, TX 75034", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1786993070629-yu46is.jpg", cuisines: ["South Indian"], instagram: "https://www.instagram.com/reel/DdAXE81MZt8/?utm_source=ig_web_copy_link&stkn=MzRlODBiNWFlZA==" },
  { name: "Birista House by PRISM BAR n GRILL", city: "Frisco", address: "6377 Custer Rd, Frisco, TX 75035", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1787952050925-p4u35e.jpg", cuisines: ["Bar & Grill"], instagram: "https://www.instagram.com/reel/DceHfRqxK07/?utm_source=ig_web_copy_link&igsi=MzRlODBiNWFlZA==" },
  { name: "LoKal Eatz", city: "Irving", address: "1951 N Belt Line Rd, Irving, TX 75061", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1790776038499-ch9wvm.png", cuisines: ["Street Food"], instagram: "https://www.instagram.com/p/DdwvtrQs78z/" },
  { name: "LoKal Eatz McKinney", city: "McKinney", address: "6631 Co Rd 124, McKinney, TX 75071", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1791398038323-1tvp0g.jpg", cuisines: ["Street Food"], instagram: "https://www.instagram.com/p/DdwvtrQs78z/" },
  { name: "SAWAII", city: "Lewisville", address: "615 FM2281 Ste 100, Lewisville, TX 75056", website: "", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1792782391497-cpk77fv.png", cuisines: ["North Indian"] },
];
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { AdminItem, AdminAddBar } from '../admin/AdminControls';
import type { FieldDef } from '../admin/AdminFieldModal';
import NewsTicker from './NewsTicker';

const RESTAURANT_FIELDS: FieldDef[] = [
  { key: 'name', label: 'Restaurant name', required: true },
  { key: 'city', label: 'City', placeholder: 'Frisco' },
  { key: 'address', label: 'Address', type: 'textarea' },
  { key: 'image', label: 'Image URL', type: 'image', placeholder: 'https://…' },
  {
    key: 'cuisines',
    label: 'Type / Cuisine',
    type: 'multiselect',
    options: ['North Indian', 'South Indian', 'Mediterranean', 'Hookah Lounge', 'Bar & Grill', 'Vegetarian', 'Fusion', 'Pakistani', 'Street Food', 'Food Trucks', 'Pizza', 'Halal', 'Others'],
    help: 'Controls which filter chips this restaurant shows under. Select as many as apply.',
  },
  { key: 'mapsUrl', label: 'Google Maps link', type: 'url' },
  { key: 'website', label: 'Website URL', type: 'url' },
  { key: 'instagram', label: 'Instagram URL', type: 'url', placeholder: 'https://instagram.com/…' },
  { key: 'facebook', label: 'Facebook URL', type: 'url', placeholder: 'https://facebook.com/…' },
  { key: 'youtube', label: 'YouTube URL', type: 'url', placeholder: 'https://youtube.com/…' },
  { key: 'tiktok', label: 'TikTok URL', type: 'url', placeholder: 'https://tiktok.com/…' },
];

const CUISINES = [
  { name: 'North Indian', icon: '🍛' },
  { name: 'South Indian', icon: '🥞' },
  { name: 'Mediterranean', icon: '🐟' },
  { name: 'Hookah Lounge', icon: '💨' },
  { name: 'Bar & Grill', icon: '🍺' },
  { name: 'Vegetarian', icon: '🌿' },
  { name: 'Fusion', icon: '✨' },
  { name: 'Pakistani', icon: '🥙' },
  { name: 'Street Food', icon: '🌯' },
  { name: 'Food Trucks', icon: '🚚' },
  { name: 'Pizza', icon: '🍕' },
  { name: 'Halal', icon: '🕌' },
  { name: 'Others', icon: '📋' },
  { name: 'Catering Only', icon: '🥡' },
];

const LOCATION_FILTERS = ['Addison', 'Allen', 'Anna', 'Arlington', 'Aubrey', 'Carrollton', 'Cedar Hill', 'Coppell', 'Dallas', 'Denton', 'DeSoto', 'Duncanville', 'Farmers Branch', 'Flower Mound', 'Fort Worth', 'Frisco', 'Garland', 'Grand Prairie', 'Highland Village', 'Hutchins', 'Irving', 'Lewisville', 'Mansfield', 'McKinney', 'Melissa', 'Mesquite', 'Midlothian', 'Plano', 'Richardson', 'Rockwall', 'Rowlett', 'Terrell', 'Other'];

const HERO_LOCATIONS = ['All Locations', 'Addison', 'Allen', 'Anna', 'Arlington', 'Aubrey', 'Carrollton', 'Cedar Hill', 'Coppell', 'Dallas', 'Denton', 'DeSoto', 'Duncanville', 'Farmers Branch', 'Flower Mound', 'Fort Worth', 'Frisco', 'Garland', 'Grand Prairie', 'Highland Village', 'Hutchins', 'Irving', 'Lewisville', 'Mansfield', 'McKinney', 'Melissa', 'Mesquite', 'Midlothian', 'Plano', 'Prosper', 'Richardson', 'Rockwall', 'Rowlett', 'Southlake', 'Terrell', 'The Colony', 'Other'];

export function toSlug(name: string) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

// The platform datatable declares `cuisines` as a `string` column, so the saved
// multi-select array comes back comma-joined ("Vegetarian,South Indian") rather
// than as an array. Anything that isn't already an array therefore has to be
// split back apart — otherwise an `Array.isArray` check fails, the admin's pick
// is discarded, and the card falls through to its ['Others'] default.
function toCuisineArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
  if (typeof v === 'string') return v.split(',').map((x) => x.trim()).filter(Boolean);
  return [];
}

// Restaurants the owner has manually marked as closed — these always show a
// red "Closed" badge on the card, overriding any live Google Places
// open/closed status.
const CLOSED_RESTAURANTS = new Set([
  'Anjappar Elite',
  'Goli Soda',
]);

// Live Google Places names can differ from the owner's label (casing,
// suffixes like "Inc" or "- Chettinad Restaurant"). Compare on a normalized
// name (lowercase, alphanumeric only) and allow containment either way, so
// a card named "Goli Soda Inc" or "ANJAPPAR ELITE" still gets the badge.
function isMarkedClosedRestaurant(name: string): boolean {
  const n = (name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  if (!n) return false;
  for (const closed of CLOSED_RESTAURANTS) {
    const c = closed.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (n === c || n.includes(c) || c.includes(n)) return true;
  }
  return false;
}

const FEATURED_RESTAURANTS = new Set([
  'Moonrise Lake Lounge',
  'Spice Rack', 'Simply South', 'Hashtag India', 'Hashtag India Lewisville', 'The Monks', 'The Monks India Fusion', 'Rotate Social',
  'The Mango Yard', 'Dil se Desi', 'Dum N Grill', 'Nags Kitchen', 'Universal Bakery',
  'The Delhi Lounge', 'Anjappar Elite', 'MNM Lounge', 'Soul Of South', 'Yum Of India',
  'DesiBites', 'Fanzo Sports Bar', 'House of Biryanis and Kebabs', 'Kuppanna', 'Marina',
  'Red Hot', 'Spicy Vihaar', 'Sri Krishna Vilas',
  'The Retro Street Bar & Kitchen', 'Cafe De Khan', 'Gilgamesh',
  'Habibi & Spice', 'Kailash Parbat', 'Prime Doner', 'Veg Vihaar', 'LoKal Eatz', 'LoKal Eatz McKinney',
  'Goli Soda', 'Gwalia', 'Hyderabad House', 'Sigree Grill', 'Garam Masala Kitchen',
  'Bawarchi Biryanis', 'Huq Up Hookah Cafe', 'Desi District', 'Habibi Royal Mandi House',
  'Fusion Republic Indian Kitchen', 'Page 3 Eatery', 'Indraprastha', 'Chennai Cafe',
  'Hyderabad Wala', 'The Pulao Company', 'Anjappar Plano', 'Sheesh Eatery & Lounge',
  'Vindu Indian Cuisine', 'GT Road', 'Dwaraka Curry Point',
  'Birista House by PRISM BAR n GRILL',
  'Pizza Twist', 'Pizza Twist Lewisville',
  'Haldirams',
]);

// Force a specific cuisine/type for restaurants that come from live Google
// Places data (name match, case-insensitive) — these override whatever
// default/category Google Places or the fallback logic would otherwise assign.
export const CUISINE_NAME_OVERRIDES: Record<string, string[]> = {
  'simply south': ['Vegetarian'],
  'bharat bhavan': ['Vegetarian'],
  "shree vimal's": ['Vegetarian'],
  'shree vimal': ['Vegetarian'],
  'desi chowrasta': ['South Indian'],
  'frisco basecamp': ['Fusion'],
  'sheesh eatery & lounge': ['Pakistani', 'Hookah Lounge', 'Halal'],
  'sheesh eatery and lounge': ['Pakistani', 'Hookah Lounge', 'Halal'],
  'the retro street bar & kitchen': ['Bar & Grill', 'Street Food', 'Fusion', 'Hookah Lounge'],
  'the retro street bar and kitchen': ['Bar & Grill', 'Street Food', 'Fusion', 'Hookah Lounge'],
};

// Manual social-link overrides for restaurants that come from live Google
// Places data (name match, case-insensitive) and don't have an admin-editable
// social field yet — e.g. a YouTube video for Goli Soda.
const SOCIAL_NAME_OVERRIDES: Record<string, { youtube?: string; instagram?: string; facebook?: string; tiktok?: string; website?: string }> = {
  'moonrise lake lounge': { instagram: 'https://www.instagram.com/p/Dbt7op9sYo7/' },
  'goli soda': { youtube: 'https://youtu.be/w-pfw385p38' },
  'gilgamesh': { instagram: 'https://www.instagram.com/p/DLX5ctcg157/' },
  'cafe de khan': { instagram: 'https://www.instagram.com/p/DGjLKhwyn-G/' },
  'gwalia': { youtube: 'https://youtu.be/RDc1kw0n_ks' },
  'habibi & spice': { instagram: 'https://www.instagram.com/p/DVt250kDygC/' },
  'kailash parbat': { instagram: 'https://www.instagram.com/p/DPHxD_wkjZY/' },
  'red hot': { youtube: 'https://youtu.be/7Dgr7OyLcic' },
  'sigree grill': { youtube: 'https://youtu.be/GDpWu0CItmc' },
  'veg vihaar': { instagram: 'https://www.instagram.com/p/DLNGCjoACye/' },
  'kuppanna': { instagram: 'https://www.instagram.com/p/C0dL3kWNMhZ/' },
  // Marina's card should not show YouTube or Facebook icons.
  'marina': { youtube: '', facebook: '' },
  'sheesh eatery & lounge': { instagram: 'https://www.instagram.com/p/DG_74uHvBg0/' },
  'dwaraka curry point': { instagram: 'https://www.instagram.com/reel/DdAXE81MZt8/?utm_source=ig_web_copy_link&stkn=MzRlODBiNWFlZA==' },
  'sheesh eatery and lounge': { instagram: 'https://www.instagram.com/p/DG_74uHvBg0/' },
  'vindu indian cuisine': { youtube: 'https://youtu.be/fIZkqeljqao' },
  // Spicy Vihaar's featured card should only show its "Featured by
  // TastebudsByAnubhi" badge — force-clear website/youtube so those icons
  // never render next to it, even if a stray admin-entered row has them.
  'spicy vihaar': { website: '', youtube: '' },
  // Spice Rack's website icon should not render on its card.
  'spice rack': { website: '' },
  // Pizza Twist (Lewisville) featured card — Instagram post shown next to the
  // "Featured by TastebudsByAnubhi" badge; website cleared so the icon shares
  // the badge row (same layout as Dum N Grill). The 'pizza twist' key is
  // address-guarded in socialOverridesFor() so the Frisco card never picks
  // these up.
  'pizza twist': { instagram: 'https://www.instagram.com/p/DdaJ_CoMxoX/', website: '' },
  'pizza twist lewisville': { instagram: 'https://www.instagram.com/p/DdaJ_CoMxoX/', website: '' },
  // LoKal Eatz featured card — Instagram post from TastebudsByAnubhi shown
  // next to the "Featured by TastebudsByAnubhi" badge. Keyed by normalized
  // name so any admin/live variant ("Lokal Eatz", "LoKal Eatz Irving")
  // still picks it up via the normalized lookup below.
  'lokal eatz': { instagram: 'https://www.instagram.com/p/DdwvtrQs78z/' },
  // Haldirams (7010 Preston Rd ste 240, Frisco) featured card — Instagram
  // post from TastebudsByAnubhi shown next to the "Featured by
  // TastebudsByAnubhi" badge, same layout as LoKal Eatz.
  'haldirams': { instagram: 'https://www.instagram.com/p/DebzEk0ssLn/' },
};

// Address-aware lookup for SOCIAL_NAME_OVERRIDES: the 'pizza twist' entry
// belongs to the Lewisville card only, so the Frisco "Pizza Twist" card keeps
// its own (empty) social row. Falls back to a normalized (lowercase,
// alphanumeric-only) match so admin/live name variants ("Lokal Eatz",
// "LoKal Eatz - Irving") still pick up their override.
function socialOverridesFor(name: string, address?: string) {
  const key = name.trim().toLowerCase();
  if (key === 'pizza twist' && !(address || '').toLowerCase().includes('lewisville')) return undefined;
  if (SOCIAL_NAME_OVERRIDES[key]) return SOCIAL_NAME_OVERRIDES[key];
  const n = key.replace(/[^a-z0-9]/g, '');
  if (n) {
    for (const [k, v] of Object.entries(SOCIAL_NAME_OVERRIDES)) {
      const c = k.replace(/[^a-z0-9]/g, '');
      if (c && c.length >= 8 && n.includes(c)) return v;
    }
  }
  return undefined;
}

// Merge the hardcoded social map onto a card as a FALLBACK ONLY. A non-empty
// value the card already carries — e.g. a YouTube/Instagram/website link the
// admin saved via the edit modal (an mc override) — ALWAYS wins; the map only
// fills blanks. (Its '' entries still suppress an unwanted icon, but only when
// the admin hasn't set one of their own.) Previously the map was spread AFTER
// the card data, so it silently clobbered every admin-entered social link —
// which is why "add a YouTube link to a restaurant" looked like it never saved.
function mergeSocial<T extends Record<string, any>>(base: T, name: string, address?: string): T {
  const ov = socialOverridesFor(name, address);
  if (!ov) return base;
  const out: any = { ...base };
  for (const [k, v] of Object.entries(ov)) {
    const cur = (base as any)[k];
    out[k] = cur != null && String(cur).trim() !== '' ? cur : v;
  }
  return out;
}

// Instagram posts from TastebudsByAnubhi backing a restaurant's Featured
// badge — the "Featured by TastebudsByAnubhi" text on the card links to the
// post. Normalized matching (same rules as isFeaturedRestaurant below).
const FEATURED_BADGE_LINKS: Record<string, string> = {
  'lokal eatz': 'https://www.instagram.com/p/DdwvtrQs78z/',
  'haldirams': 'https://www.instagram.com/p/DebzEk0ssLn/',
};

export function featuredBadgeLinkFor(name: string): string | undefined {
  const n = (name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  if (!n) return undefined;
  for (const [key, url] of Object.entries(FEATURED_BADGE_LINKS)) {
    const c = key.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (c && (n === c || (c.length >= 8 && (n.includes(c) || c.includes(n))))) return url;
  }
  return undefined;
}

export function isFeaturedRestaurant(r: { name: string; address?: string }) {
  if (!FEATURED_RESTAURANTS.has(r.name)) {
    // Robust fallback: live/admin names can differ in casing, spacing or
    // suffixes ("Lokal Eatz", "LoKal Eatz - Irving"). Compare normalized
    // (lowercase, alphanumeric only); containment is allowed only for
    // longer featured names so short entries like 'Marina' can't
    // false-match unrelated cards.
    const n = (r.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    let matched = false;
    if (n) {
      for (const f of FEATURED_RESTAURANTS) {
        const c = f.toLowerCase().replace(/[^a-z0-9]/g, '');
        if (c && (n === c || (c.length >= 8 && (n.includes(c) || c.includes(n))))) {
          matched = true;
          break;
        }
      }
    }
    if (!matched) return false;
  }
  if (r.name === 'The Monks' && r.address?.includes('790 W Exchange Pkwy')) return false;
  if (r.name === 'Bawarchi' && r.address?.includes('3400 W Campbell Rd')) return false;
  // Bawarchi (Irving, 8150 N MacArthur Blvd) is not Featured — it belongs in
  // the "More Restaurants" grid.
  if ((r.name || '').toLowerCase().includes('bawarchi') && (r.address || '').toLowerCase().includes('8150 n macarthur')) return false;
  // Hyderabad Wala Irving (10045 N MacArthur Blvd) and Hyderabad Wala Colony
  // (5733 State Hwy 121, The Colony) are not Featured — they belong in the
  // "More Restaurants" grid.
  if ((r.name || '').toLowerCase().includes('hyderabad wala') && ((r.address || '').toLowerCase().includes('10045 n macarthur') || (r.address || '').toLowerCase().includes('5733 state hwy 121'))) return false;
  // Only the Lewisville Pizza Twist is Featured — the Frisco card of the same
  // name must stay in the "More Restaurants" grid.
  if (r.name === 'Pizza Twist' && !(r.address || '').toLowerCase().includes('lewisville')) return false;
  // Only the Frisco Spice Rack is Featured — the Plano card (2865 McDermott Rd)
  // must stay in the "More Restaurants" grid.
  if ((r.name || '').toLowerCase().includes('spice rack') && (r.address || '').toLowerCase().includes('2865 mcdermott')) return false;
  return true;
}

// ── Shared "all restaurants" hook ──
// Mirrors the exact merge (Google Places + admin custom_restaurants +
// food_trucks + hookah_lounges managed collections) that the Restaurants page
// uses to build its grid, so any OTHER consumer (e.g. global search) sees the
// same live, admin-editable list — including restaurants added purely via the
// admin "Add Restaurant" form that never touch Google Places at all.
export function useAllRestaurantPlaces(location?: string) {
  const { places, loading, error } = useGooglePlaces(location);
  const mc = useManagedCollection<any>('restaurants', places, (r) => slugify(r.id || r.placeId || r.name));
  const ftMc = useManagedCollection<any>('food_trucks', FOOD_TRUCKS, (ft, i) => slugify(`${ft.name}-${ft.address}` || String(i)));
  const foodTruckPlaces: PlaceResult[] = ftMc.items.map((ft: any, i: number) => ({
    ...ft,
    id: `ft-${ft._mcId || i}`,
    location: ft.city || 'Dallas',
    city: ft.city || 'Dallas',
    address: ft.address || '',
    rating: 0,
    reviewCount: 0,
    image: ft.image || 'https://images.unsplash.com/photo-1565123409695-7b5ef63a2efb?w=600&h=400&fit=crop',
    placeId: `ft-${ft._mcId || i}`,
    website: ft.website || '',
    mapsUrl: ft.mapsUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${ft.name}, ${ft.address}`)}`,
    cuisines: ['Food Trucks'],
    _mc: ftMc,
  }));
  const hlMc = useManagedCollection<any>('hookah_lounges', HOOKAH_LOUNGES, (hl, i) => slugify(`${hl.name}-${hl.address}` || String(i)));
  const hookahPlaces: PlaceResult[] = hlMc.items.map((hl: any, i: number) => ({
    ...hl,
    id: `hl-${hl._mcId || i}`,
    location: hl.city || 'Dallas',
    city: hl.city || 'Dallas',
    address: hl.address || '',
    rating: 0,
    reviewCount: 0,
    image: hl.image || 'https://images.unsplash.com/photo-1571896769668-a9e7d0b67b27?w=600&h=400&fit=crop',
    placeId: `hl-${hl._mcId || i}`,
    website: hl.website || '',
    mapsUrl: hl.mapsUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${hl.name}, ${hl.address}`)}`,
    cuisines: ['Hookah Lounge'],
    _mc: hlMc,
  }));
  const crMc = useManagedCollection<any>('custom_restaurants', CUSTOM_RESTAURANTS, (cr, i) => slugify(`${cr.name}-${cr.address}` || String(i)));
  const restaurantsWithDefaults = mc.items.map((r: any) => {
    const nameOverride = CUISINE_NAME_OVERRIDES[(r.name || '').trim().toLowerCase()];
    return {
      ...mergeSocial(r, r.name || '', r.address),
      cuisines: toCuisineArray(r.cuisines).length > 0 ? toCuisineArray(r.cuisines) : (nameOverride || ['Others']), // Admin-picked type(s) win; else name override; else default
      mapsUrl: r.mapsUrl || (r.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(r.address)}` : ''),
      image: r.image || 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=600&h=400&fit=crop',
      _mc: mc,
    };
  });
  const customPlaces: PlaceResult[] = crMc.items.map((cr: any, i: number) => ({
    ...cr,
    id: `cr-${cr._mcId || i}`,
    location: cr.city || 'Frisco',
    city: cr.city || 'Frisco',
    address: cr.address || '',
    rating: 0,
    reviewCount: 0,
    image: cr.image,
    placeId: `cr-${cr._mcId || i}`,
    website: cr.website || '',
    mapsUrl: cr.mapsUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${cr.name}, ${cr.address}`)}`,
    cuisines: toCuisineArray(cr.cuisines).length > 0 ? toCuisineArray(cr.cuisines) : (cr.cuisine ? [cr.cuisine] : ['Pizza']),
    _mc: crMc,
  }));
  const allItems: any[] = [...restaurantsWithDefaults, ...foodTruckPlaces, ...hookahPlaces, ...customPlaces];
  const REMOVED_NAMES = new Set(['Huq Up', 'Hashtag Indiaa', 'Lokaleatz']);
  // Hard block: a stray row can persist in the mc_custom_restaurants data
  // table (written by a prior admin add/edit) even after every source-code
  // reference to it is removed, since that table lives independently of this
  // file. Filter "GT Road Restaurant" (the duplicate ghost) out by exact name
  // so it can never render regardless of what's sitting in the database, but
  // leave the real "GT Road" card (now in CUSTOM_RESTAURANTS) alone.
  const isBlockedGhost = (r: any) =>
    /^gt\s*road\s*restaurant$/i.test(String(r.name || '').trim());
  const keepByName = new Map<string, any>();
  for (const r of allItems) {
    if (REMOVED_NAMES.has(r.name)) continue;
    if (isBlockedGhost(r)) continue;
    const key = `${(r.name || '').toLowerCase()}|${(r.address || '').toLowerCase().replace(/\s+/g, ' ').trim()}`;
    const cur = keepByName.get(key);
    const rKey = String(r._mcId || r.id || r.name || '');
    if (!cur || rKey < String(cur._mcId || cur.id || cur.name || '')) keepByName.set(key, r);
  }
  const keptSet = new Set(keepByName.values());
  const dedupedItems = allItems.filter((r) => keptSet.has(r));
  return { items: dedupedItems as PlaceResult[], loading, error };
}

// Navigate with the URL only, like every other section. Router state is
// structured-cloned by history.pushState, and a restaurant carries `_mc` (the
// admin collection handle — functions), which made pushState throw
// DataCloneError and silently abort the click. The detail page finds the
// restaurant by slug (name-only fallback for entries without a city).
export function RestaurantCard({ restaurant, index, imageClass }: { restaurant: PlaceResult; index: number; imageClass?: string }) {
  const navigate = useNavigate();
  const slug = toSlug(restaurant.name);
  const isFeatured = isFeaturedRestaurant(restaurant);
  // Instagram post from TastebudsByAnubhi backing the Featured badge (if
  // any) — the badge text itself links to the post.
  const featuredLink = featuredBadgeLinkFor(restaurant.name);
  // Owner-marked closed (CLOSED_RESTAURANTS) always wins over the live
  // Google Places open/closed status.
  const isMarkedClosed = isMarkedClosedRestaurant(restaurant.name);
  const socialLinksItem = restaurant;
  const socialCount = countSocialLinks(socialLinksItem);
  // Share one compact row with the "Featured by" text only when there's a
  // single icon (e.g. Instagram-only, like Dum N Grill). Cards with more
  // than one icon (e.g. Spice Rack) get their own dedicated line so the
  // icons have room and don't crowd the featured label.
  const shareRow = socialCount <= 1;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="bg-white rounded-md border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col cursor-pointer"
      style={{ fontFamily: "'Lora', Georgia, serif" }}
      onClick={() => navigate(`/restaurants/${toSlug(restaurant.name)}-${toSlug(restaurant.city || restaurant.location || '')}`.replace(/-+$/, ''))}
    >
      <div className={`w-full ${imageClass || "h-[90px]"} relative overflow-hidden`}>
        <img
          src={restaurant.image}
          alt={`${restaurant.name} Indian restaurant in ${restaurant.city || 'Dallas'}`}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=600&h=400&fit=crop';
          }}
        />
        {isMarkedClosed ? (
          <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-600 text-white shadow-sm">
            Closed
          </div>
        ) : restaurant.openNow !== undefined && (
          <div className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold ${restaurant.openNow ? 'bg-green-500 text-white' : 'bg-gray-600 text-white'}`}>
            {restaurant.openNow ? 'Open Now' : 'Closed'}
          </div>
        )}
      </div>
      <div className="p-2 pb-1 flex-1">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/restaurants/${toSlug(restaurant.name)}-${toSlug(restaurant.city || restaurant.location || '')}`.replace(/-+$/, ''));
          }}
          className="text-left w-full"
        >
          <h3 className="text-[15px] font-bold text-gray-900 leading-tight truncate mb-0.5 hover:text-orange-600 transition-colors">{restaurant.name}</h3>
        </button>
        <div className="flex items-center gap-1 mb-1">
          <MapPin className="w-3 h-3 text-orange-500 shrink-0" />
          <span className="text-[11px] text-gray-500 font-medium">{restaurant.address}</span>
        </div>
      </div>
      {/* Featured badge + social icons share a single row when there's just
          one icon (e.g. Instagram only, like Dum N Grill) — no wasted line.
          Cards with more than one icon (e.g. Spice Rack) get the icons on
          their own line below so they have room to breathe. SocialLinks
          renders nothing when no URL was entered, so rows collapse cleanly
          on cards without any links or featured badge. */}
      {shareRow ? (
        (isFeatured || socialCount > 0) && (
          <div className="px-2 pt-1 pb-1 flex items-center justify-between gap-2 min-h-[22px]">
            {isFeatured ? (
              <span className="flex items-center gap-1 min-w-0">
                {featuredLink ? (
                  <a
                    href={featuredLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-[10px] font-semibold text-green-600 tracking-wide truncate hover:text-green-700 hover:underline"
                    data-hs-id="restaurant-featured-badge"
                  >
                    Featured by TastebudsByAnubhi
                  </a>
                ) : (
                  <p className="text-[10px] font-semibold text-green-600 tracking-wide truncate">
                    Featured by TastebudsByAnubhi
                  </p>
                )}
              </span>
            ) : <span />}
            <SocialLinks item={socialLinksItem} px={22} />
          </div>
        )
      ) : (
        <>
          {isFeatured && (
            <div className="px-2 pt-1 pb-0.5 flex items-center gap-1">
              {featuredLink ? (
                <a
                  href={featuredLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-[10px] font-semibold text-green-600 tracking-wide truncate hover:text-green-700 hover:underline"
                  data-hs-id="restaurant-featured-badge"
                >
                  Featured by TastebudsByAnubhi
                </a>
              ) : (
                <p className="text-[10px] font-semibold text-green-600 tracking-wide truncate">
                  Featured by TastebudsByAnubhi
                </p>
              )}
            </div>
          )}
          <div className="px-2 pb-1 pt-0.5">
            <SocialLinks item={socialLinksItem} px={22} />
          </div>
        </>
      )}
      <div className="px-2 pb-2 pt-1 flex gap-1.5">
        <a
          href={restaurant.mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 flex items-center justify-center gap-1 bg-[#f97316] hover:bg-[#ea580c] text-white text-[11px] font-semibold py-1.5 rounded-md transition-colors duration-200"
        >
          <ExternalLink className="w-3 h-3" />
          View Location
        </a>
      </div>
    </motion.div>
  );
}

export default function Restaurants() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedHeroLocation, setSelectedHeroLocation] = useState('All Locations');
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownSearch, setDropdownSearch] = useState('');
  const [selectedCuisine, setSelectedCuisine] = useState<string | null>(null);
  const [isDescExpanded, setIsDescExpanded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownInputRef = useRef<HTMLInputElement>(null);

  const effectiveLocation = selectedHeroLocation !== 'All Locations' ? selectedHeroLocation : null;
  const displayLocation = effectiveLocation ?? 'Dallas-DFW';

  const { places, loading, error, usingFallback, refetch } = useGooglePlaces(effectiveLocation ?? undefined);

  const mc = useManagedCollection<any>('restaurants', places, (r) => slugify(r.id || r.placeId || r.name));

  // Food trucks — managed collection, converted to PlaceResult shape for unified display
  const ftMc = useManagedCollection<any>('food_trucks', FOOD_TRUCKS, (ft, i) => slugify(`${ft.name}-${ft.address}` || String(i)));
  const foodTruckPlaces: PlaceResult[] = ftMc.items.map((ft: any, i: number) => ({
    ...ft,
    id: `ft-${ft._mcId || i}`,
    location: ft.city || 'Dallas',
    city: ft.city || 'Dallas',
    address: ft.address || '',
    rating: 0,
    reviewCount: 0,
    image: ft.image || 'https://images.unsplash.com/photo-1565123409695-7b5ef63a2efb?w=600&h=400&fit=crop',
    placeId: `ft-${ft._mcId || i}`,
    website: ft.website || '',
    mapsUrl: ft.mapsUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${ft.name}, ${ft.address}`)}`,
    cuisines: ['Food Trucks'],
    _mc: ftMc,
  }));

  // Hookah lounges — managed collection, converted to PlaceResult shape for unified display
  const hlMc = useManagedCollection<any>('hookah_lounges', HOOKAH_LOUNGES, (hl, i) => slugify(`${hl.name}-${hl.address}` || String(i)));
  const hookahPlaces: PlaceResult[] = hlMc.items.map((hl: any, i: number) => ({
    ...hl,
    id: `hl-${hl._mcId || i}`,
    location: hl.city || 'Dallas',
    city: hl.city || 'Dallas',
    address: hl.address || '',
    rating: 0,
    reviewCount: 0,
    image: hl.image || 'https://images.unsplash.com/photo-1571896769668-a9e7d0b67b27?w=600&h=400&fit=crop',
    placeId: `hl-${hl._mcId || i}`,
    website: hl.website || '',
    mapsUrl: hl.mapsUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${hl.name}, ${hl.address}`)}`,
    cuisines: ['Hookah Lounge'],
    _mc: hlMc,
  }));

  // Custom restaurants — managed collection. Previously this was a plain hardcoded
  // array with NO store backing, so any admin edit (e.g. adding an Instagram link)
  // was written into the Google-Places `mc` collection (since every card in the grid
  // rendered with a hardcoded mc={mc}) and silently discarded on the next render
  // because there was no matching default item there to attach the override to.
  const crMc = useManagedCollection<any>('custom_restaurants', CUSTOM_RESTAURANTS, (cr, i) => slugify(`${cr.name}-${cr.address}` || String(i)));

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
        setDropdownSearch('');
      }
    }
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      setTimeout(() => dropdownInputRef.current?.focus(), 50);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  const filteredHeroLocations = HERO_LOCATIONS.filter((loc) =>
    loc.toLowerCase().includes(dropdownSearch.toLowerCase())
  );

  // Ensure custom restaurants have required fields for display
  const restaurantsWithDefaults = mc.items.map((r: any) => {
    // A stale admin-override row saved BEFORE a name correction (e.g. old
    // "Prism Bar & Grill" override) can still carry the old `name` field and
    // that override wins over the live/corrected default — silently
    // reverting a rename and, since isFeaturedRestaurant() matches on the
    // exact name string, also dropping the card out of Featured. Re-run
    // every card's name through the same correction map on every render so
    // no old DB row can ever resurrect a superseded name.
    const name = normalizeName(r.name || '');
    const nameOverride = CUISINE_NAME_OVERRIDES[name.trim().toLowerCase()];
    return {
      ...mergeSocial(r, name, r.address),
      name,
      cuisines: toCuisineArray(r.cuisines).length > 0 ? toCuisineArray(r.cuisines) : (r.cuisine ? [r.cuisine] : (nameOverride || ['Others'])), // Admin-picked type(s) win; else name override; else default
      mapsUrl: r.mapsUrl || (r.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(r.address)}` : ''),
      image: r.image || 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=600&h=400&fit=crop',
      _mc: mc,
    };
  });

  // Custom restaurants — managed collection, converted to PlaceResult shape for unified display
  const customPlaces: PlaceResult[] = crMc.items.map((cr: any, i: number) => {
    return {
      ...mergeSocial(cr, cr.name || '', cr.address),
      id: `cr-${cr._mcId || i}`,
      location: cr.city || 'Frisco',
      city: cr.city || 'Frisco',
      address: cr.address || '',
      rating: 0,
      reviewCount: 0,
      image: cr.image,
      placeId: `cr-${cr._mcId || i}`,
      website: cr.website || '',
      mapsUrl: cr.mapsUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${cr.name}, ${cr.address}`)}`,
      cuisines: toCuisineArray(cr.cuisines).length > 0 ? toCuisineArray(cr.cuisines) : ['Pizza'],
      _mc: crMc,
    };
  });

  // Merge restaurant places + food truck places + hookah lounges + custom into one unified list
  const allItems: any[] = [...restaurantsWithDefaults, ...foodTruckPlaces, ...hookahPlaces, ...customPlaces];

  // Remove specific cards + deduplicate Cavalli / Mirage Hookah Lounge.
  // Pick ONE instance per name DETERMINISTICALLY (by id), independent of the
  // current sort order — otherwise admin drag-swap could change which duplicate
  // "wins" and make cards appear/disappear (and the list reshuffle).
  const REMOVED_NAMES = new Set(['Huq Up', 'Hashtag Indiaa', 'Lokaleatz']);
  const keepByName = new Map<string, any>();
  for (const r of allItems) {
    if (REMOVED_NAMES.has(r.name)) continue;
    // Key by name + address (not name alone) — chains like "Simply South" or
    // "Hyderabad House" have multiple distinct locations that must all survive.
    // Only truly duplicate cards (same name AND same address) collapse to one.
    const key = `${(r.name || '').toLowerCase()}|${(r.address || '').toLowerCase().replace(/\s+/g, ' ').trim()}`;
    const cur = keepByName.get(key);
    const rKey = String(r._mcId || r.id || r.name || '');
    if (!cur || rKey < String(cur._mcId || cur.id || cur.name || '')) keepByName.set(key, r);
  }
  const keptSet = new Set(keepByName.values());
  const dedupedItems = allItems.filter((r) => keptSet.has(r));

  // ── Featured status by STABLE identity ──
  // isFeaturedRestaurant() matches by the card's CURRENT name, so renaming a
  // card in admin (e.g. "Spice Rack" -> "Spice Racks") ejected it from the
  // Featured section. Precompute the featured cards' stable `_mcId`s from the
  // DEFAULT data (same id fns the managed collections use) so featured status
  // no longer depends on the edited name.
  const isDefFeatured = (name: string, address?: string) => {
    if (!FEATURED_RESTAURANTS.has(name)) return false;
    if (name === 'The Monks' && address?.includes('790 W Exchange Pkwy')) return false;
    if (name === 'Bawarchi' && address?.includes('3400 W Campbell Rd')) return false;
    // Bawarchi (Irving, 8150 N MacArthur Blvd) is not Featured — belongs in
    // the "More Restaurants" grid. Guards the stable-id path.
    if ((name || '').toLowerCase().includes('bawarchi') && (address || '').toLowerCase().includes('8150 n macarthur')) return false;
    // Hyderabad Wala Irving (10045 N MacArthur Blvd) and Hyderabad Wala Colony
    // (5733 State Hwy 121) are not Featured — they belong in the "More
    // Restaurants" grid. Guards the stable-id path.
    if ((name || '').toLowerCase().includes('hyderabad wala') && ((address || '').toLowerCase().includes('10045 n macarthur') || (address || '').toLowerCase().includes('5733 state hwy 121'))) return false;
    if (name === 'Pizza Twist' && !(address || '').toLowerCase().includes('lewisville')) return false;
    // Plano Spice Rack (2865 McDermott Rd) is not Featured — only the Frisco
    // Spice Rack is. Guards the stable-id path in case a live/admin variant of
    // the Plano card is named exactly "Spice Rack".
    if ((name || '').toLowerCase().includes('spice rack') && (address || '').toLowerCase().includes('2865 mcdermott')) return false;
    return true;
  };
  const featuredIds = new Set<string>();
  (places || []).forEach((d: any) => { if (isDefFeatured(d.name, d.address)) featuredIds.add(slugify(d.id || d.placeId || d.name)); });
  CUSTOM_RESTAURANTS.forEach((d) => { if (isDefFeatured(d.name, d.address)) featuredIds.add(slugify(`${d.name}-${d.address}`)); });
  FOOD_TRUCKS.forEach((d) => { if (isDefFeatured(d.name, d.address)) featuredIds.add(slugify(`${d.name}-${d.address}`)); });
  HOOKAH_LOUNGES.forEach((d) => { if (isDefFeatured(d.name, d.address)) featuredIds.add(slugify(`${d.name}-${d.address}`)); });
  // Featured if the STABLE id is a known featured card (edit-proof), or — for
  // live Google places not in the defaults — the current name still matches.
  const isFeatured = (r: any) => (r._mcId && featuredIds.has(r._mcId)) || isFeaturedRestaurant(r);

  // ── Unified cross-collection ordering ──
  // The grid mixes 4 managed collections (restaurants / food_trucks / hookah /
  // custom), each with its own per-collection order-space. To let an admin drag
  // ANY card to ANY position across them, we use a single `xorder` value on a
  // shared numeric scale. Baseline = the card's index in the natural merged
  // list (stable, doesn't depend on drag); a dragged card gets an explicit
  // fractional `xorder` (midpoint of its new neighbours), persisted into its OWN
  // collection store. Sorting the grid by (xorder ?? baseIdx) then interleaves
  // cards from different collections correctly.
  const baseIdx = new Map<string, number>();
  dedupedItems.forEach((r, i) => baseIdx.set(String(r._mcId ?? r.id), i));
  const effOrder = (r: any): number => {
    const x = Number(r?.xorder);
    return Number.isFinite(x) ? x : (baseIdx.get(String(r?._mcId ?? r?.id)) ?? 0);
  };
  const cardById = new Map<string, any>();
  dedupedItems.forEach((r) => cardById.set(String(r._mcId ?? r.id), r));

  // Reposition `draggedId` before/after `targetId` within its section, persisting
  // a fractional xorder to the dragged card's own collection store.
  const reorderRestaurant = (draggedId: string, targetId: string, before: boolean) => {
    const dragged = cardById.get(String(draggedId));
    const target = cardById.get(String(targetId));
    if (!dragged || !target || !dragged._mc) return;
    // Section the dragged card belongs to, sorted by current effective order.
    const section = (isFeatured(dragged) ? featuredRestaurants : nonFeaturedRestaurants);
    const without = section.filter((r: any) => String(r._mcId ?? r.id) !== String(draggedId));
    const tIdx = without.findIndex((r: any) => String(r._mcId ?? r.id) === String(targetId));
    if (tIdx === -1) return;
    let newX: number;
    if (before) {
      const prev = without[tIdx - 1];
      newX = prev ? (effOrder(prev) + effOrder(target)) / 2 : effOrder(target) - 1;
    } else {
      const next = without[tIdx + 1];
      newX = next ? (effOrder(target) + effOrder(next)) / 2 : effOrder(target) + 1;
    }
    if (newX === effOrder(dragged)) return; // no-op
    return dragged._mc.saveEdit(dragged, { xorder: newX });
  };

  const filteredRestaurants = dedupedItems.filter((r) => {
    const matchesQuery = searchQuery.trim() === '' || r.name.toLowerCase().includes(searchQuery.toLowerCase());
    const cuisineList: string[] = r.cuisines || [];
    // Every Pakistani-tagged restaurant also shows under Halal (per user request),
    // in addition to any restaurant explicitly tagged Halal via its cuisines.
    const isHalalRestaurant = cuisineList.includes('Halal') || cuisineList.includes('Pakistani');
    const matchesCuisine = !selectedCuisine || (selectedCuisine === 'Halal' ? isHalalRestaurant : cuisineList.includes(selectedCuisine));
    // Location filter: when a specific city is selected in the hero dropdown,
    // only keep cards whose city/address actually match it. Google Places
    // results are already scoped server-side by `effectiveLocation`, but the
    // merged custom restaurants / food trucks / hookah lounges are not, so we
    // must check them here too. "All Locations" (effectiveLocation === null)
    // shows everything, as before.
    const matchesLocation = !effectiveLocation || (() => {
      const loc = effectiveLocation.toLowerCase();
      const city = (r.city || r.location || '').toLowerCase();
      const address = (r.address || '').toLowerCase();
      return city.includes(loc) || address.includes(loc);
    })();
    return matchesQuery && matchesCuisine && matchesLocation;
  });

  // Featured section and "more" section, each ordered by the unified xorder scale
  // so cross-collection drag-reordering within a section sticks.
  const featuredRestaurants = filteredRestaurants
    .filter((r) => isFeatured(r))
    .sort((a, b) => effOrder(a) - effOrder(b));

  // Pin "Moonrise Lake Lounge" right after the first "Simply South" card in the
  // Featured row (same row, first row) — admin-added cards otherwise sort to
  // the end of their own collection via xorder/baseIdx.
  const moonriseIdx = featuredRestaurants.findIndex((r) => (r.name || '').trim().toLowerCase() === 'moonrise lake lounge');
  if (moonriseIdx !== -1) {
    const [moonrise] = featuredRestaurants.splice(moonriseIdx, 1);
    const simplySouthIdx = featuredRestaurants.findIndex((r) => (r.name || '').trim().toLowerCase() === 'simply south');
    featuredRestaurants.splice(simplySouthIdx === -1 ? 0 : simplySouthIdx + 1, 0, moonrise);
  }

  // Pin "Birista House by PRISM BAR n GRILL" to the start of the second row
  // (the grid is 4-wide at lg, so index 4 is the first card of row 2).
  const biristaIdx = featuredRestaurants.findIndex((r) => (r.name || '').trim().toLowerCase() === 'birista house by prism bar n grill');
  if (biristaIdx !== -1) {
    const [birista] = featuredRestaurants.splice(biristaIdx, 1);
    featuredRestaurants.splice(Math.min(4, featuredRestaurants.length), 0, birista);
  }

  // Pin "Pizza Twist" (Lewisville) into the second row of the Featured grid,
  // right after Birista House (index 5 = second card of row 2 at lg 4-wide).
  const pizzaTwistIdx = featuredRestaurants.findIndex((r) => {
    const n = (r.name || '').trim().toLowerCase();
    return n.startsWith('pizza twist') && (n.includes('lewisville') || (r.address || '').toLowerCase().includes('lewisville'));
  });
  if (pizzaTwistIdx !== -1) {
    const [pizzaTwist] = featuredRestaurants.splice(pizzaTwistIdx, 1);
    featuredRestaurants.splice(Math.min(5, featuredRestaurants.length), 0, pizzaTwist);
  }

  const nonFeaturedRestaurants = filteredRestaurants
    .filter((r) => !isFeatured(r))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const hasActiveFilters = !!selectedCuisine;

  const restaurantsJsonLd = [
    {
      '@type': 'CollectionPage',
      name: 'Indian Restaurants Near Me in Dallas-Fort Worth',
      description:
        'Find the best Indian restaurants near you in Dallas, Frisco, Plano, Irving, McKinney, Allen, Carrollton, Coppell, Richardson, and across DFW. Browse South Indian, North Indian, Pakistani, Halal, biryani, and Indo-Pak restaurants, food trucks, and hookah lounges.',
      url: 'https://dallasdesiscoop.com/restaurants',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://dallasdesiscoop.com/' },
        { '@type': 'ListItem', position: 2, name: 'Restaurants', item: 'https://dallasdesiscoop.com/restaurants' },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Indian Restaurants Near Me in Dallas, Frisco, Plano & DFW | Dallas Desi Scoop"
        description="Discover the best Indian restaurants near you across Dallas-Fort Worth, including Frisco, Plano, Irving, McKinney, Allen, Carrollton, Coppell, and Richardson. Browse South Indian, North Indian, Pakistani, Halal, biryani spots, food trucks, and hookah lounges."
        canonical="/restaurants"
        jsonLd={restaurantsJsonLd}
      />
      <style>{`
        .filter-scroll::-webkit-scrollbar { display: none; }
        .filter-scroll { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* Hero */}
      <section className="relative w-full" style={{ minHeight: "160px" }}>
        <div className="absolute inset-0 overflow-hidden bg-black" style={{ containerType: "size" as any }}>
          <img
            src="https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1787922872538-uvjgxr.png"
            alt=""
            className="absolute top-1/2 left-1/2 object-cover"
            style={{ width: "100cqh", height: "100cqw", transform: "translate(-50%, -50%) rotate(90deg)" }}
          />
          <div className="absolute inset-0 bg-black/45" />
          <svg viewBox="0 0 1440 24" preserveAspectRatio="none" style={{ position: 'absolute', left: 0, bottom: '-1px', width: '100%', height: '18px', display: 'block' }}>
            <path d="M0,0 Q720,24 1440,0 L1440,24 L0,24 Z" fill="#ffffff" />
          </svg>
        </div>
        <div className="relative flex flex-col items-center justify-center pt-3 sm:pt-4 pb-8" style={{ zIndex: 10, minHeight: "160px" }}>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="w-full max-w-[740px]">
            <div className="rounded-md" style={{ boxShadow: '0 0 40px 12px rgba(255,255,255,0.45), 0 0 80px 24px rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)' }}>
              <div className="flex flex-col sm:flex-row bg-white rounded-md shadow-md h-auto sm:h-[40px]">
                <div className="flex-1 px-3 py-2 sm:py-0 flex items-center border-b sm:border-b-0 sm:border-r border-gray-200 min-h-[40px]">
                  <span className="text-[11px] text-gray-600 font-semibold shrink-0 mr-2 hidden sm:inline">which restaurant?</span>
                  <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Restaurant name" className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent" />
                </div>
                <div ref={dropdownRef} className="relative border-b sm:border-b-0 sm:border-r border-gray-200 min-h-[40px] sm:w-[170px] md:w-[190px] shrink-0" style={{ zIndex: 99999 }}>
                  <button onClick={() => setShowDropdown(!showDropdown)} className="w-full h-full px-3 py-2 sm:py-0 flex items-center gap-1.5 text-left min-h-[40px]">
                    <MapPin className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                    <span className="text-[12px] text-gray-700 font-medium truncate flex-1">{selectedHeroLocation}</span>
                    <svg className={'w-3 h-3 text-gray-400 shrink-0 transition-transform ' + (showDropdown ? 'rotate-180' : '')} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showDropdown && (
                    <div
                      className="absolute top-full left-0 bg-white border border-gray-100 rounded-lg overflow-hidden"
                      style={{ zIndex: 99999, marginTop: '4px', width: '200px', boxShadow: '0 10px 24px rgba(15,23,42,0.16)', fontFamily: "'Lora', Georgia, serif" }}
                    >
                      <div className="px-3 py-2 border-b border-gray-100">
                        <input
                          ref={dropdownInputRef}
                          type="text"
                          value={dropdownSearch}
                          onChange={(e) => setDropdownSearch(e.target.value)}
                          placeholder="Search locations..."
                          className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent"
                        />
                      </div>
                      <div className="overflow-y-auto" style={{ maxHeight: '200px' }}>
                        {filteredHeroLocations.length === 0 ? (
                          <div className="px-3 py-4 text-center text-[12px] text-gray-400">No locations found</div>
                        ) : (
                          filteredHeroLocations.map((loc) => (
                            <button
                              key={loc}
                              onClick={() => { setSelectedHeroLocation(loc); setShowDropdown(false); setDropdownSearch(''); }}
                              className="w-full text-left flex items-center transition-colors duration-150"
                              style={{
                                padding: '8px 12px',
                                fontSize: '13px',
                                fontWeight: selectedHeroLocation === loc ? 700 : 500,
                                color: selectedHeroLocation === loc ? '#f97316' : '#111827',
                                background: selectedHeroLocation === loc ? '#fff3e8' : 'transparent',
                                height: '36px',
                              }}
                              onMouseEnter={e => { if (selectedHeroLocation !== loc) (e.currentTarget as HTMLButtonElement).style.background = '#fff7ed'; }}
                              onMouseLeave={e => { if (selectedHeroLocation !== loc) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                            >
                              {loc}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <button onClick={() => setShowDropdown(false)} className="bg-[#f97316] hover:bg-[#ea580c] text-white text-[13px] font-bold px-5 py-2.5 sm:py-0 transition-colors duration-200 shrink-0 min-h-[40px] sm:rounded-r-md w-full sm:w-auto">
                  <span className="flex items-center justify-center gap-1.5">
                    <Search className="w-3.5 h-3.5 sm:hidden" />
                    Search
                  </span>
                </button>
              </div>
            </div>


          </motion.div>
        </div>
      </section>
      <NewsTicker />

      {/* Secondary Category Nav */}
      <CategoryNav />

      {/* ── Filter Bar ── */}
      <div className="bg-white border-b border-gray-100">
        <div className="page-container pt-6 pb-4">

          {/* Explore by Type */}
          <div>
            <p className="text-[11px] font-bold text-gray-400 uppercase mb-2.5" style={{ fontFamily: "'Lora', Georgia, serif", letterSpacing: '0.12em' }}>
              Explore by Type
            </p>
            <div className="flex gap-2 overflow-x-auto filter-scroll pb-1 flex-wrap">
              {CUISINES.map((c) => {
                const active = selectedCuisine === c.name;
                // 'Catering Only' is a cross-link to the Catering page, not a
                // grid filter. Render a real <a> (react-router Link) so both
                // users and crawlers can follow it.
                if (c.name === 'Catering Only') {
                  return (
                    <Link
                      key={c.name}
                      to="/catering"
                      className="flex items-center gap-1.5 rounded-full border whitespace-nowrap transition-colors duration-150 shrink-0"
                      style={{
                        fontFamily: "'Lora', Georgia, serif",
                        height: '35px',
                        padding: '0 13px',
                        fontSize: '12.5px',
                        fontWeight: 600,
                        background: '#ffffff',
                        color: '#111827',
                        borderColor: '#e5e7eb',
                        boxShadow: 'none',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f0fdf4';
                        e.currentTarget.style.borderColor = '#86efac';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#ffffff';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                      }}
                    >
                      <span className="text-[14px] leading-none">{c.icon}</span>
                      {c.name}
                    </Link>
                  );
                }
                return (
                  <button
                    key={c.name}
                    onClick={() => setSelectedCuisine(active ? null : c.name)}
                    className="flex items-center gap-1.5 rounded-full border whitespace-nowrap transition-colors duration-150 shrink-0"
                    style={{
                      fontFamily: "'Lora', Georgia, serif",
                      height: '35px',
                      padding: '0 13px',
                      fontSize: '12.5px',
                      fontWeight: 600,
                      background: active ? '#16a34a' : '#ffffff',
                      color: active ? '#ffffff' : '#111827',
                      borderColor: active ? '#16a34a' : '#e5e7eb',
                      boxShadow: 'none',
                    }}
                    onMouseEnter={(e) => {
                      if (!active) {
                        e.currentTarget.style.background = '#f0fdf4';
                        e.currentTarget.style.borderColor = '#86efac';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!active) {
                        e.currentTarget.style.background = '#ffffff';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                      }
                    }}
                  >
                    <span className="text-[14px] leading-none">{c.icon}</span>
                    {c.name}
                  </button>
                );
              })}

              {/* Clear Filters */}
              {hasActiveFilters && (
                <button
                  onClick={() => { setSelectedCuisine(null); }}
                  className="flex items-center gap-1.5 rounded-full border border-dashed whitespace-nowrap transition-colors duration-150 shrink-0"
                  style={{
                    fontFamily: "'Lora', Georgia, serif",
                    height: '35px',
                    padding: '0 13px',
                    fontSize: '12.5px',
                    fontWeight: 600,
                    color: '#6b7280',
                    background: '#ffffff',
                    borderColor: '#d1d5db',
                    boxShadow: 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#f97316';
                    e.currentTarget.style.borderColor = '#f97316';
                    e.currentTarget.style.background = '#fff7ed';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#6b7280';
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.background = '#ffffff';
                  }}
                >
                  <span className="text-[12px] leading-none">✕</span>
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Restaurant Listings ── */}
      <section className="bg-gray-50/50 py-8">
        <div className="page-container">
          <div className="flex flex-col gap-6">
            {/* Main content */}
            <div className="flex-1 min-w-0">
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
                <h1 className="text-[16px] sm:text-[18px] font-bold text-gray-900 mb-1" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                  {selectedCuisine === "North Indian"
                    ? "Discover Top North Indian Restaurants in Dallas–Fort Worth"
                    : selectedCuisine === "Pakistani"
                    ? "Discover Top Pakistani Restaurants in Dallas–Fort Worth"
                    : selectedCuisine === "Street Food"
                    ? "Discover Top Street Food Restaurants in Dallas–Fort Worth"
                    : selectedCuisine === "Hookah Lounge"
                    ? "Discover Top Desi Hookah Lounges in Dallas–Fort Worth"
                    : selectedCuisine === "Fusion"
                    ? "Discover Top Desi Fusion Restaurants in Dallas–Fort Worth"
                    : selectedCuisine === "Bar & Grill"
                    ? "Discover Top Indian Bar & Grill Restaurants in Dallas–Fort Worth"
                    : selectedCuisine === "Food Trucks"
                    ? "Discover Top Indian Food Trucks in Dallas–Fort Worth"
                    : selectedCuisine === "Pizza"
                    ? "Discover Top Indian Fusion Pizza in Dallas–Fort Worth"
                    : selectedCuisine === "Halal"
                    ? "Discover Top Halal Restaurants in Dallas–Fort Worth"
                    : selectedCuisine
                    ? `Explore the Top ${selectedCuisine} Restaurants in the Dallas–Fort Worth Area`
                    : "Top Indian, Pakistani & Mediterranean Restaurants in Dallas–Fort Worth"}
                </h1>
                {(() => {
                  const descriptionParagraphs =
                    selectedCuisine === "North Indian"
                      ? [
                          "Explore the rich flavors of North India across Dallas–Fort Worth, from Punjabi and Mughlai cuisine to popular Delhi, Rajasthani, Gujarati, and North Indian vegetarian restaurants.",
                          "Discover favorites like butter chicken, tandoori dishes, kebabs, naan, chaat, samosas, paneer, biryani, thalis, and authentic regional specialties from restaurants across Dallas, Frisco, Plano, Irving, McKinney, Carrollton, Richardson, and throughout the DFW area.",
                          "Whether you're searching for Punjabi restaurants in Dallas, North Indian restaurants in Frisco, Mughlai food in DFW, Rajasthani restaurants in Plano, Gujarati restaurants in Dallas, or North Indian food near you, find local restaurants and authentic regional flavors on Dallas Desi Scoop.",
                        ]
                      : selectedCuisine === "Pakistani"
                      ? [
                          "Explore the rich and authentic flavors of Pakistani cuisine across Dallas–Fort Worth, from Lahori and Karachi-style food to Punjabi, Mughlai, and traditional Pakistani specialties.",
                          "Discover favorites like biryani, nihari, haleem, karahi, kebabs, seekh kebabs, chicken tikka, tandoori dishes, paratha, naan, chaat, and Pakistani desserts from restaurants across Dallas, Frisco, Plano, Irving, Carrollton, Richardson, McKinney, and throughout the DFW area.",
                          "Whether you're searching for Pakistani restaurants in Dallas, Pakistani food in Frisco, halal Pakistani restaurants in Plano, biryani in Irving, Karachi-style food in DFW, or Pakistani food near you, find local Pakistani restaurants and authentic flavors on Dallas Desi Scoop.",
                        ]
                      : selectedCuisine === "South Indian"
                      ? [
                          "Explore the flavors of South India across Dallas–Fort Worth, from spicy Chettinad and Andhra cuisine to traditional Tamil, Telugu, Kerala, Karnataka, and South Indian vegetarian restaurants.",
                          "Discover dosa, idli, vada, sambar, biryani, thali, filter coffee, and authentic regional specialties from restaurants across Dallas, Frisco, Plano, Irving, McKinney, Carrollton, Richardson, and the DFW area.",
                          "Whether you're searching for Chettinad restaurants in Dallas, Andhra restaurants in DFW, Telugu restaurants in Frisco, Tamil restaurants in Plano, Kerala restaurants in Dallas, or South Indian food near you, find local restaurants and authentic regional flavors on Dallas Desi Scoop.",
                        ]
                      : selectedCuisine === "Street Food"
                      ? [
                          "Explore the vibrant flavors of Indian street food across Dallas–Fort Worth, from spicy chaat and savory snacks to popular street-style favorites from different regions of India.",
                          "Find favorites like pani puri, golgappa, pani poori, bhel puri, sev puri, dahi puri, papdi chaat, aloo tikki chaat, samosa chaat, ragda pattice, kachori, vada pav, pav bhaji, dabeli, misal pav, samosas, pakoras, and Indian street-style sandwiches.",
                          "Whether you're searching for Indian street food in Dallas, chaat in Frisco, pani puri in Plano, samosa chaat in Irving, vada pav in McKinney, or Indian snacks near you, discover local street food restaurants, chaat spots, food trucks, and authentic Indian flavors on Dallas Desi Scoop.",
                        ]
                      : selectedCuisine === "Hookah Lounge"
                      ? [
                          "Explore Desi hookah lounges and South Asian-inspired lounges across Dallas–Fort Worth, offering a social atmosphere, Indian and Pakistani flavors, music, entertainment, and late-night experiences.",
                          "Find Desi hookah lounges, Indian hookah lounges, Pakistani hookah lounges, hookah bars, and South Asian lounges in Dallas, Frisco, Plano, Irving, McKinney, Carrollton, Richardson, and throughout the DFW area.",
                          "Whether you're searching for Desi hookah lounges in Dallas, Indian hookah lounges in Frisco, Pakistani hookah lounges in Plano, hookah lounges near you, or late-night Desi lounges in DFW, discover local spots for food, drinks, music, hookah, and social gatherings on Dallas Desi Scoop.",
                        ]
                      : selectedCuisine === "Fusion"
                      ? [
                          "Explore creative Indian fusion restaurants across Dallas–Fort Worth, blending traditional South Asian flavors with modern American and international cuisines.",
                          "Discover favorites like Indian fusion tacos, Desi burgers, fusion pizza, Indo-Chinese dishes, Indian-inspired pasta, fusion biryani, creative chaat, Desi street food, and innovative desserts.",
                          "Whether you're searching for Indian fusion restaurants in Dallas, Desi fusion food in Frisco, Indo-Chinese restaurants in Irving, or unique fusion food in DFW, discover creative restaurants and modern takes on Desi cuisine on Dallas Desi Scoop.",
                        ]
                      : selectedCuisine === "Vegetarian"
                      ? [
                          "Explore delicious Indian vegetarian restaurants across Dallas–Fort Worth, serving authentic flavors from North Indian, South Indian, Gujarati, Rajasthani, Punjabi, and regional Indian cuisines.",
                          "Discover favorites like dosa, idli, vada, thali, chaat, samosa, pav bhaji, paneer dishes, dal, curries, biryani, puri, poori, uttapam, and traditional vegetarian meals.",
                          "Whether you're searching for vegetarian Indian restaurants in Dallas, pure vegetarian restaurants in Frisco, South Indian vegetarian food in Plano, Gujarati, Marathi restaurants in Irving, or vegetarian Indian food near you, find local restaurants serving authentic meat-free Indian cuisine across the DFW area.",
                        ]
                      : selectedCuisine === "Mediterranean"
                      ? [
                          "Explore Mediterranean restaurants across Dallas–Fort Worth, serving flavorful Greek, Lebanese, Turkish, Persian, and Middle Eastern-inspired cuisine. Discover shawarma, kebabs, hummus, falafel, gyros, grilled meats, fresh salads, and more across Dallas, Frisco, Plano, Irving, McKinney, and the DFW area.",
                        ]
                      : selectedCuisine === "Bar & Grill"
                      ? [
                          "Explore Indian bars and grills across Dallas–Fort Worth, serving flavorful Indian food, grilled specialties, kebabs, tandoori dishes, biryani, curries, appetizers, cocktails, and more. Find Indian bar & grill restaurants in Dallas, Frisco, Plano, Irving, McKinney, and across the DFW area.",
                        ]
                      : selectedCuisine === "Food Trucks"
                      ? [
                          "Find Indian food trucks across Dallas–Fort Worth serving flavorful street food, chaat, biryani, dosa, samosas, kebabs, Indo-Chinese dishes, snacks, desserts, and more. Discover Indian food trucks and mobile food vendors in Dallas, Frisco, Plano, Irving, McKinney, Carrollton, and throughout the DFW area.",
                        ]
                      : selectedCuisine === "Pizza"
                      ? [
                          "Explore Indian fusion pizza across Dallas–Fort Worth, combining classic pizza with bold Indian flavors. Discover creative pizzas featuring tandoori chicken, butter chicken, tikka, paneer, Indian spices, chutneys, and Desi-inspired toppings in Dallas, Frisco, Plano, Irving, McKinney, and throughout the DFW area.",
                        ]
                      : selectedCuisine === "Halal"
                      ? [
                          "Explore halal restaurants across Dallas–Fort Worth serving halal meat and halal-certified or halal-focused cuisine. Find restaurants offering Indian, Pakistani, Desi, Mediterranean, Middle Eastern, and other halal-friendly dishes made with flavorful ingredients and authentic spices.",
                          "Discover popular halal favorites including biryani, kebabs, tandoori chicken, curries, nihari, karahi, grilled meats, shawarma, gyros, burgers, wraps, and more. Whether you're looking for a halal Indian restaurant in Dallas, halal Pakistani food in Frisco, halal restaurants in Plano, halal food in Irving, or halal restaurants near you, find local dining options across the DFW area on Dallas Desi Scoop.",
                          "Browse halal restaurants in Dallas, Frisco, Plano, Irving, McKinney, Carrollton, Richardson, Allen, and throughout the Dallas–Fort Worth metroplex. Find your next halal dining spot for family meals, casual dining, celebrations, takeout, and everyday cravings.",
                        ]
                      : [
                          "Discover Indian, Pakistani, Mediterranean, Middle Eastern, and Desi restaurants across Dallas–Fort Worth. Explore authentic Indian food, Pakistani cuisine, biryani, South Indian and North Indian restaurants, vegetarian dining, halal restaurants, Mediterranean food, Middle Eastern cuisine, and more. Whether you're craving traditional regional dishes, family-friendly dining, street food, or modern Desi flavors, find restaurants and local dining spots across the DFW area.",
                        ];

                  const [firstParagraph, ...restParagraphs] = descriptionParagraphs;

                  return (
                    <>
                      {/* Mobile: first paragraph + Read more toggle */}
                      <div className="sm:hidden">
                        <p className="text-[12px] text-gray-500 mb-2" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                          {firstParagraph}
                        </p>
                        {restParagraphs.length > 0 && (
                          <>
                            {isDescExpanded && restParagraphs.map((para, i) => (
                              <p key={i} className="text-[12px] text-gray-500 mb-2" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                                {para}
                              </p>
                            ))}
                            <button
                              type="button"
                              onClick={() => setIsDescExpanded((v) => !v)}
                              className="text-[12px] font-semibold mb-4"
                              style={{ fontFamily: "'Lora', Georgia, serif", color: '#f97316' }}
                            >
                              {isDescExpanded ? 'Read less' : 'Read more'}
                            </button>
                          </>
                        )}
                        {restParagraphs.length === 0 && <div className="mb-4" />}
                      </div>

                      {/* Desktop / tablet: full text, no truncation */}
                      <div className="hidden sm:block">
                        {descriptionParagraphs.map((para, i) => (
                          <p
                            key={i}
                            className={`text-[13px] text-gray-500 ${i === descriptionParagraphs.length - 1 ? 'mb-6' : 'mb-2'}`}
                            style={{ fontFamily: "'Lora', Georgia, serif" }}
                          >
                            {para}
                          </p>
                        ))}
                      </div>
                    </>
                  );
                })()}
                {/* Loading state */}
                {loading && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                    {[...Array(8)].map((_, i) => (
                      <div key={i} className="bg-white rounded-md border border-gray-100 shadow-sm overflow-hidden">
                        <div className="w-full h-[90px] bg-gray-200 animate-pulse" />
                        <div className="p-2 pb-1">
                          <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4 mb-1.5" />
                          <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2 mb-0.5" />
                          <div className="h-3 bg-gray-200 rounded animate-pulse w-1/3" />
                        </div>
                        <div className="px-2 pb-2 pt-1 flex gap-1.5">
                          <div className="h-7 bg-gray-200 rounded animate-pulse flex-1" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Error state */}
                {error && !loading && (
                  <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
                    <div className="text-3xl mb-2">⚠️</div>
                    <p className="text-[14px] font-semibold text-gray-700 mb-1" style={{ fontFamily: "'Lora', Georgia, serif" }}>Unable to load restaurants</p>
                    <p className="text-[12px] text-gray-400 mb-4" style={{ fontFamily: "'Lora', Georgia, serif" }}>{error}</p>
                    <button
                      onClick={() => refetch()}
                      className="px-4 py-2 rounded-lg bg-orange-50 border border-orange-200 text-orange-600 text-[12px] font-semibold hover:bg-orange-100 transition-colors"
                      style={{ fontFamily: "'Lora', Georgia, serif" }}
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {/* Restaurant grid */}
                {!loading && !error && (
                  <>
                    <AdminAddBar mc={mc} fields={RESTAURANT_FIELDS} label="Add Restaurant" />

                    {/* Featured by TastebudsByAnubhi */}
                    {featuredRestaurants.length > 0 && (
                      <div className="mb-8">
                        <h3 className="text-[14px] sm:text-[16px] font-bold text-green-700 mb-3" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                          Featured by TastebudsByAnubhi
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                          {featuredRestaurants.map((r, i) => (
                            <AdminItem key={r._key} mc={r._mc || mc} item={r} fields={RESTAURANT_FIELDS} title="Restaurant" dragGroup="rest-featured" onReorder={reorderRestaurant}>
                              <RestaurantCard restaurant={r as any} index={i} />
                            </AdminItem>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* More Restaurants */}
                    {nonFeaturedRestaurants.length > 0 && (
                      <div>
                        {featuredRestaurants.length > 0 && (
                          <h3 className="text-[14px] sm:text-[16px] font-bold text-gray-700 mb-3" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                            More Restaurants
                          </h3>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                          {nonFeaturedRestaurants.map((r, i) => (
                            <AdminItem key={r._key} mc={r._mc || mc} item={r} fields={RESTAURANT_FIELDS} title="Restaurant" dragGroup="rest-more" onReorder={reorderRestaurant}>
                              <RestaurantCard restaurant={r as any} index={i} />
                            </AdminItem>
                          ))}
                        </div>
                      </div>
                    )}

                    {filteredRestaurants.length === 0 && (
                      <div className="text-center py-16">
                        <div className="text-4xl mb-3">🍽️</div>
                        <p className="text-[15px] font-semibold text-gray-700 mb-1" style={{ fontFamily: "'Lora', Georgia, serif" }}>No restaurants found for this filter.</p>
                        <p className="text-[12px] text-gray-400" style={{ fontFamily: "'Lora', Georgia, serif" }}>Try a different cuisine or location.</p>
                        {hasActiveFilters && (
                          <button
                            onClick={() => { setSelectedCuisine(null); setSelectedHeroLocation("All Locations"); }}
                            className="mt-4 px-5 py-2 rounded-full bg-orange-50 border border-orange-200 text-orange-600 text-[12px] font-semibold hover:bg-orange-100 transition-colors"
                            style={{ fontFamily: "'Lora', Georgia, serif" }}
                          >
                            Clear Filters
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
                {/* Blog guide links — bottom of page */}
                <div className="mt-10 pt-6 border-t border-gray-100" data-hs-id="restaurants-blog-links">
                  <p className="text-[13px] text-gray-600 leading-relaxed mb-3" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                    Explore restaurants in Dallas, Frisco, Plano, Irving, McKinney, Carrollton, Richardson, Allen, The Colony, Prosper, and throughout the DFW metroplex. Browse restaurants by cuisine and city to find your next favorite place to eat, whether you're searching for Indian restaurants in Dallas, Pakistani restaurants in Frisco, Mediterranean restaurants in Plano, Middle Eastern food in Irving, halal restaurants in DFW, or Desi food near you. Explore Indian restaurants serving popular favorites such as biryani, pulao, pakora, samosa, dosa, idli, curries, tandoori dishes, vegetarian specialties and more.
                  </p>
                  <p className="mb-2" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                    <Link
                      to="/blog/best-indian-restaurants-dfw"
                      className="text-[13px] font-semibold underline underline-offset-2"
                      style={{ color: '#f97316' }}
                    >
                      Read our DFW restaurant guide →
                    </Link>
                  </p>
                  <p style={{ fontFamily: "'Lora', Georgia, serif" }}>
                    <Link
                      to="/blog"
                      className="text-[13px] font-semibold underline underline-offset-2"
                      style={{ color: '#f97316' }}
                    >
                      Explore all DFW Desi guides on our blog →
                    </Link>
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
